<template>
  <wui-card
    v-if="isShortReactionsView"
    class="w-[280px]"
    state="text"
    shadow="overflow"
  >
    <wui-list :size="32">
      <template
        v-for="(option, index) in options"
        :key="index"
      >
        <hr
          v-if="option.dividerBefore"
          class="mx-1 my-1 border-t-bor-nav"
        >
        <hover-submenu
          v-if="isDesktopHoverOption(option.emit)"
          :data-test="`chat-context-${option.emit}-submenu`"
        >
          <wui-list-item
            :icon="option.icon"
            :data-test="`chat-context-${option.emit}`"
            v-bind="option.variant ? { variant: option.variant } : {}"
            @click="onOptionClick(option)"
          >
            <span class="grow">{{ option.name }}</span>
            <message-views-avatar-stack
              v-if="option.emit === Emits.VIEW_VIEWS"
              :viewers="viewers"
            />
          </wui-list-item>
          <template #submenu>
            <message-views-flyout
              v-if="option.emit === Emits.VIEW_VIEWS"
              :event-id="event.eventId"
              :p-served="event.item.pServed"
            />
            <message-reactions-flyout
              v-else-if="option.emit === Emits.VIEW_REACTIONS"
              :event-id="event.eventId"
            />
          </template>
        </hover-submenu>
        <wui-list-item
          v-else
          :icon="option.icon"
          :data-test="`chat-context-${option.emit}`"
          v-bind="option.variant ? { variant: option.variant } : {}"
          @click="onOptionClick(option)"
        >
          <span class="grow">{{ option.name }}</span>
          <message-views-avatar-stack
            v-if="option.emit === Emits.VIEW_VIEWS"
            :viewers="viewers"
          />
          <wui-icon
            v-else-if="option.withChevron"
            name="chevronRightLgM"
            class="ml-auto shrink-0 text-fg-base-fg17"
            small
          />
        </wui-list-item>
      </template>
      <copy-image
        v-if="selectedFile && isImageFile(selectedFile)"
        :file-cookie="selectedFile"
      />
      <download-file
        v-if="event.item.files && event.item.files.length"
        :files="event.item.files"
        :path-name="'/files/'"
      >
        <template #default="{ download }">
          <wui-list-item
            icon="downloadM"
            :class="{ 'h-auto!': voiceFileMeta }"
            @click="download"
          >
            <span class="flex flex-col">
              {{ event.item.files.length > 1 ? t('common.downloadAll') : t('common.download') }}
              <span
                v-if="voiceFileMeta"
                class="text-label-r-12-18 text-fg-base-fg14"
                data-test="voice-message-file-meta"
              >
                {{ voiceFileMeta }}
              </span>
            </span>
          </wui-list-item>
        </template>
      </download-file>
      <remove-file
        v-if="selectedFile && canRemoveFile"
        :cookie="selectedFile"
        :event="event"
      >
        <template #default="{ remove }">
          <wui-list-item
            icon="trashM"
            variant="negative"
            @click="remove"
          >
            {{ t('common.removeFile') }}
          </wui-list-item>
        </template>
      </remove-file>
      <template
        v-if="messageSourceInfo"
        #footer
      >
        <wui-list-item disabled>
          <span class="flex items-center gap-1 text-body-r-14-20 text-fg-base-fg22">
            {{ t('chat.sentFrom') }}
            <wui-icon
              :name="messageSourceInfo.icon"
              :title="messageSourceInfo.text"
            />
          </span>
        </wui-list-item>
      </template>
    </wui-list>
  </wui-card>
</template>

<script setup lang="ts">
import { IconName, WuiCard, WuiIcon, WuiList, WuiListItem } from '@wui/common-library'
import { HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent, MessageStatusType } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'


import { CopyImage } from '@/features/copy-image'
import { DownloadFile } from '@/features/download-file'
import { MessageReactionsFlyout } from '@/features/message-reactions-viewer'
import { MessageViewsAvatarStack, MessageViewsFlyout } from '@/features/message-views-viewer'
import { RemoveFile } from '@/features/remove-file'

import { usePinEventsStore } from '@/entities/messages'
import { useReactionsStore } from '@/entities/reaction'
import { useStoreLastReadEventIds } from '@/entities/room'
import { getExtensionFromMimeType, isVoiceMessage } from '@/entities/voice-message'

import { HoverSubmenu } from '@/shared/components/hover-submenu'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { isMobileLayout } from '@/shared/composables/use-mobile'
import { bytesConverter } from '@/shared/utils/bytes-converter'
import { isImageFile } from '@/shared/utils/files'
import { PLATFORM } from '@/shared/utils/platform'

const props = defineProps<{
  event: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>,
  selectedFile: string | null,
  isLink?: boolean
  isSelected?: boolean
  isSelectionMode?: boolean
  hideQuote?: boolean
  hideDelete?: boolean
  showDeleteForSelf?: boolean
  hideMarkAsUnread?: boolean
  hideViews?: boolean
  isTextSelected?: boolean
  eventsLength?: number
  showInChat?: boolean
}>()

enum Emits {
  COPY_MESSAGE = 'copyMessage',
  COPY_SELECTED_MESSAGE_TEXT = 'copySelectedMessageText',
  COPY_LINK = 'copyLink',
  QUOTE_MESSAGE = 'quoteMessage',
  PIN_MESSAGE = 'pinMessage',
  UNPIN_MESSAGE = 'unpinMessage',
  REMOVE_MESSAGE = 'removeMessage',
  REMOVE_MESSAGE_FOR_SELF = 'removeMessageForSelf',
  START_EDIT = 'startEdit',
  HIDE_MENU = 'hideMenu',
  FORWARD_MESSAGE = 'forwardMessage',
  SELECT_MULTIPLE = 'selectMultiple',
  RESEND = 'resend',
  MARK_AS_UNREAD = 'markAsUnread',
  DELETE_MARK_AS_UNREAD = 'deleteMarkAsUnread',
  SHOW_IN_CHAT = 'showInChat',
  VIEW_REACTIONS = 'viewReactions',
  VIEW_VIEWS = 'viewViews',
}

type Option = {
  name: string
  emit: Emits
  icon: IconName
  variant?: 'negative'
  dividerBefore?: boolean
  withChevron?: boolean
}
type MessageSourceInfo = {
  icon: IconName
  text: string
}

const emits = defineEmits<{
  (event: Emits): void
}>()

const { t } = useLocalization()
const { getPinEventsForRoom } = usePinEventsStore()
const reactionsStore = useReactionsStore()
const { isShortReactionsView, reactionEvents } = storeToRefs(reactionsStore)
const lastReadStore = useStoreLastReadEventIds()
const {
  lastMessageEventIdByRoomAndContactId,
  readTimestampsByMessageEventId,
} = storeToRefs(lastReadStore)
const { getMarkedUnread, getViewersByMessageEventId } = lastReadStore
const { currentUser } = useCurrentUser()

const hasReactions = computed(() => Boolean(reactionEvents.value.get(props.event.eventId)?.size))

const viewers = computed(() => {
  void lastMessageEventIdByRoomAndContactId.value[props.event.item.pServed]
  void readTimestampsByMessageEventId.value
  return getViewersByMessageEventId(props.event.item.pServed, props.event.eventId) ?? []
})

const hasViews = computed(() => viewers.value.length > 0)

const isDesktopHoverOption = (emit: Emits) => (
  !isMobileLayout.value
  && (emit === Emits.VIEW_VIEWS || emit === Emits.VIEW_REACTIONS)
)

const onOptionClick = (option: Option) => {
  emits(option.emit)
  if (option.emit !== Emits.VIEW_REACTIONS && option.emit !== Emits.VIEW_VIEWS) {
    emits(Emits.HIDE_MENU)
  }
}

const pinEvents = computed(() => getPinEventsForRoom(props.event.item.pServed))

const isMarkedUnread = computed(() => getMarkedUnread(props.event.item.pServed)?.eventId === props.event.eventId)

const isPinned = computed(() => {
  return pinEvents.value?.find(i => i.eventId === props.event.eventId)
})

const canRemoveFile = computed(() => {
  const { item: { files, type, messageText }} = props.event
  if (files?.length) {
    return type === 'OutgoingMessageEvent' && (files?.length > 1 || messageText)
  }
  return false
})

const voiceFileMeta = computed(() => {
  if (!isVoiceMessage(props.event.item)) return ''

  const file = props.event.item.files?.[0]
  if (!file) return ''

  const extension = file.filename.split('.').at(-1)?.toUpperCase()
    || (file.filemime ? getExtensionFromMimeType(file.filemime).toUpperCase() : '')
  const size = typeof file.filesize === 'number' ? String(bytesConverter(file.filesize)) : ''

  return [extension, size].filter(Boolean).join(' ')
})

const getMessageSourceInfo = (useragent?: string, fallbackText?: string): MessageSourceInfo | null => {
  if (!useragent) {
    if (!fallbackText) {
      return null
    }

    return { icon: 'web', text: fallbackText }
  }

  const normalizedUserAgent = useragent.toLocaleLowerCase()
  if (normalizedUserAgent.includes(PLATFORM.ASTRA_LINUX)) {
    return { icon: 'astraLinux', text: t('chat.astraLinuxDescription') }
  }
  if (normalizedUserAgent.includes(PLATFORM.LINUX)) {
    return { icon: 'linux', text: t('chat.linuxDescription') }
  }
  if (normalizedUserAgent.includes(PLATFORM.MACOS)) {
    return { icon: 'macos', text: t('chat.macOSDescription') }
  }
  if (normalizedUserAgent.includes(PLATFORM.IOS)) {
    return { icon: 'macos', text: t('chat.iOSDescription') }
  }
  if (normalizedUserAgent.includes(PLATFORM.ANDROID)) {
    return { icon: 'android', text: t('chat.androidDescription') }
  }
  if (normalizedUserAgent.includes(PLATFORM.WINDOWS)) {
    return { icon: 'win', text: t('chat.windowsDescription') }
  }
  if (normalizedUserAgent.includes(PLATFORM.BOT)) {
    return { icon: 'bot', text: t('chat.botDescription') }
  }
  if (normalizedUserAgent.includes(PLATFORM.NODE_JS)) {
    return { icon: 'nodeJS', text: t('chat.nodeJSDescription') }
  }

  return { icon: 'web', text: t('chat.webDescription') }
}

const messageSourceInfo = computed<MessageSourceInfo | null>(() => {
  if (props.event.item.type === 'IncomingMessageEvent') {
    const incomingMessage = props.event.item as MessageIncomingEvent & { useragent?: string }
    return getMessageSourceInfo(incomingMessage.useragent)
  }

  if (props.event.item.type === 'OutgoingMessageEvent') {
    const outgoingMessage = props.event.item as MessageOutgoingEvent & { useragent?: string }
    return getMessageSourceInfo(outgoingMessage.useragent, t('chat.sentFromCurrentDevice'))
  }

  return null
})

const pushOption = (options: Option[], option: Option) => {
  options.push(option)
}

const createGroupDivider = () => {
  let started = false
  return () => {
    if (started) return false
    started = true
    return true
  }
}

const selectOptionName = computed(() => {
  if (props.isSelected) return t('chat.clearSelection')
  if (props.isSelectionMode) return t('common.select')
  return t('chat.messageContextSelect')
})

const incomingMessageOptions = computed<Option[]>(() => {
  const options: Option[] = []
  const group2Divider = createGroupDivider()
  const group3Divider = createGroupDivider()
  const group4Divider = createGroupDivider()

  // Group 1: Ответить, Переслать
  if (!props.hideQuote) {
    pushOption(options, {
      name: t('chat.messageContextReply'),
      emit: Emits.QUOTE_MESSAGE,
      icon: 'arrowAnswerM',
    })
  }

  pushOption(options, {
    name: t('common.forwardMessage'),
    emit: Emits.FORWARD_MESSAGE,
    icon: 'arrowResendM',
  })

  // Group 2: Копировать текст, Закрепить, Выделить
  if (props.event.item.messageText) {
    pushOption(options, {
      name: t('chat.messageContextCopyText'),
      emit: Emits.COPY_MESSAGE,
      icon: 'copyM',
      dividerBefore: group2Divider(),
    })
  }

  if (props.isTextSelected) {
    pushOption(options, {
      name: t('common.copySelected'),
      emit: Emits.COPY_SELECTED_MESSAGE_TEXT,
      icon: 'txtFormatM',
      dividerBefore: group2Divider(),
    })
  }

  if (props.isLink) {
    pushOption(options, {
      name: t('common.copyLink'),
      emit: Emits.COPY_LINK,
      icon: 'linkM',
      dividerBefore: group2Divider(),
    })
  }

  pushOption(options, {
    name: isPinned.value ? t('common.unpin') : t('common.pin'),
    emit: isPinned.value ? Emits.UNPIN_MESSAGE : Emits.PIN_MESSAGE,
    icon: isPinned.value ? 'pinOffM' : 'pinM',
    dividerBefore: group2Divider(),
  })

  if (props.eventsLength && props.eventsLength > 1) {
    pushOption(options, {
      name: selectOptionName.value,
      emit: Emits.SELECT_MULTIPLE,
      icon: 'checkRoundM',
      dividerBefore: group2Divider(),
    })
  }

  // Group 3: Удалить (для себя)
  if (props.showDeleteForSelf && !props.hideDelete) {
    pushOption(options, {
      name: t('chat.messageContextDelete'),
      emit: Emits.REMOVE_MESSAGE_FOR_SELF,
      icon: 'trashM',
      variant: 'negative',
      dividerBefore: group3Divider(),
    })
  }

  // Group 4: Реакции, Просмотры + доп. пункты
  if (hasReactions.value) {
    pushOption(options, {
      name: t('common.reactions'),
      emit: Emits.VIEW_REACTIONS,
      icon: 'heartM',
      dividerBefore: group4Divider(),
    })
  }

  if (hasViews.value && !props.hideViews) {
    pushOption(options, {
      name: t('common.views'),
      emit: Emits.VIEW_VIEWS,
      icon: 'checksM',
      dividerBefore: group4Divider(),
    })
  }

  if (!props.hideMarkAsUnread) {
    pushOption(options, {
      name: isMarkedUnread.value ? t('common.markAsRead') : t('chat.markAsUnread'),
      emit: isMarkedUnread.value ? Emits.DELETE_MARK_AS_UNREAD : Emits.MARK_AS_UNREAD,
      icon: isMarkedUnread.value ? 'messageReadM' : 'messageUnreadM',
      dividerBefore: group4Divider(),
    })
  }

  if (props.showInChat) {
    pushOption(options, {
      name: t('chat.showInChat'),
      emit: Emits.SHOW_IN_CHAT,
      icon: 'eyeM',
      dividerBefore: group4Divider(),
    })
  }

  return options
})

const outgoingMessageOptions = computed<Option[]>(() => {
  const options: Option[] = []
  const group2Divider = createGroupDivider()
  const group3Divider = createGroupDivider()
  const group4Divider = createGroupDivider()

  // Group 1: Ответить, Переслать (+ переотправка при ошибке)
  if (props.event.status === MessageStatusType.ERROR) {
    pushOption(options, {
      name: t('chat.resend'),
      emit: Emits.RESEND,
      icon: 'arrowResendM',
    })
  }

  if (!props.hideQuote) {
    pushOption(options, {
      name: t('chat.messageContextReply'),
      emit: Emits.QUOTE_MESSAGE,
      icon: 'arrowAnswerM',
    })
  }

  pushOption(options, {
    name: t('common.forwardMessage'),
    emit: Emits.FORWARD_MESSAGE,
    icon: 'arrowResendM',
  })

  // Group 2: Копировать текст, Закрепить, Выделить
  if (props.event.item.messageText) {
    pushOption(options, {
      name: t('chat.messageContextCopyText'),
      emit: Emits.COPY_MESSAGE,
      icon: 'copyM',
      dividerBefore: group2Divider(),
    })
  }

  if (props.isTextSelected) {
    pushOption(options, {
      name: t('common.copySelected'),
      emit: Emits.COPY_SELECTED_MESSAGE_TEXT,
      icon: 'txtFormatM',
      dividerBefore: group2Divider(),
    })
  }

  if (props.isLink) {
    pushOption(options, {
      name: t('common.copyLink'),
      emit: Emits.COPY_LINK,
      icon: 'linkM',
      dividerBefore: group2Divider(),
    })
  }

  pushOption(options, {
    name: isPinned.value ? t('common.unpin') : t('common.pin'),
    emit: isPinned.value ? Emits.UNPIN_MESSAGE : Emits.PIN_MESSAGE,
    icon: isPinned.value ? 'pinOffM' : 'pinM',
    dividerBefore: group2Divider(),
  })

  if (props.eventsLength && props.eventsLength > 1) {
    pushOption(options, {
      name: selectOptionName.value,
      emit: Emits.SELECT_MULTIPLE,
      icon: 'checkRoundM',
      dividerBefore: group2Divider(),
    })
  }

  // Group 3: Изменить, Удалить
  if (!isVoiceMessage(props.event.item)) {
    pushOption(options, {
      name: t('chat.messageContextEdit'),
      emit: Emits.START_EDIT,
      icon: 'editM',
      dividerBefore: group3Divider(),
    })
  }

  if (!props.hideDelete) {
    pushOption(options, {
      name: t('chat.messageContextDelete'),
      emit: Emits.REMOVE_MESSAGE,
      icon: 'trashM',
      variant: 'negative',
      dividerBefore: group3Divider(),
    })
  }

  // Group 4: Реакции, Просмотры + доп. пункты
  if (hasReactions.value) {
    pushOption(options, {
      name: t('common.reactions'),
      emit: Emits.VIEW_REACTIONS,
      icon: 'heartM',
      dividerBefore: group4Divider(),
    })
  }

  if (hasViews.value && !props.hideViews) {
    pushOption(options, {
      name: t('common.views'),
      emit: Emits.VIEW_VIEWS,
      icon: 'checksM',
      dividerBefore: group4Divider(),
    })
  }

  if (props.showInChat) {
    pushOption(options, {
      name: t('chat.showInChat'),
      emit: Emits.SHOW_IN_CHAT,
      icon: 'eyeM',
      dividerBefore: group4Divider(),
    })
  }

  return options
})

const options = computed<Option[]>(() => props.event.item.type === 'OutgoingMessageEvent' || (props.event.item.type === 'IncomingMessageEvent' && (props.event.item as MessageIncomingEvent).executor === currentUser.value?.imLogin) ? outgoingMessageOptions.value : incomingMessageOptions.value)
</script>
